import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Cookie, Shield, Settings } from 'lucide-react';
import { openCookiePreferences } from './CookieBanner';

export const CookiePolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Cookie Policy | AIS Express</title>
        <meta
          name="description"
          content="AIS Express cookie policy — how we use cookies on aisexpress.co.uk, your rights under UK GDPR, and how to manage your preferences."
        />
        <link rel="canonical" href="https://aisexpress.co.uk/cookies" />
      </Helmet>

      <section className="min-h-screen bg-gradient-to-br from-slate-50 to-emerald-50 py-12" data-testid="cookie-policy-page">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
          <div className="text-center mb-10">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-emerald-100 text-emerald-800 text-sm font-medium mb-4">
              <Cookie className="h-4 w-4 mr-2" />
              <span>Legal</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-3">
              Cookie Policy
            </h1>
            <p className="text-slate-600">Last updated: February 2026</p>
          </div>

          <Card className="shadow-lg">
            <CardContent className="p-6 sm:p-10 space-y-8 text-slate-700 leading-relaxed">
              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-3">1. About this policy</h2>
                <p>
                  This Cookie Policy explains how <strong>AIS Express</strong> ("we", "us", "our")
                  uses cookies and similar technologies on <strong>aisexpress.co.uk</strong>. It
                  forms part of our Privacy Policy and complies with the UK General Data Protection
                  Regulation (UK GDPR) and the Privacy and Electronic Communications Regulations
                  (PECR).
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-3">2. What are cookies?</h2>
                <p>
                  Cookies are small text files stored on your device when you visit a website. They
                  help the site function properly and allow the site owner to learn how visitors
                  interact with the site. Similar technologies include local storage, pixels, and
                  session recording.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-3">3. Cookies we use</h2>

                <div className="space-y-5 mt-4">
                  <div className="border-l-4 border-emerald-500 pl-5 py-2 bg-emerald-50/40 rounded-r-lg">
                    <h3 className="font-semibold text-slate-900 text-lg mb-2 flex items-center">
                      <Shield className="h-4 w-4 mr-2 text-emerald-700" />
                      Essential Cookies (always on)
                    </h3>
                    <p className="text-sm mb-3">
                      Required for the website to function. Without these, features like the
                      booking form, order tracking, and admin login wouldn't work. These do not
                      require your consent under UK law.
                    </p>
                    <table className="w-full text-sm border-collapse">
                      <thead>
                        <tr className="border-b border-slate-300 text-left">
                          <th className="py-2 pr-3 font-semibold">Name</th>
                          <th className="py-2 pr-3 font-semibold">Purpose</th>
                          <th className="py-2 font-semibold">Duration</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b border-slate-200">
                          <td className="py-2 pr-3 font-mono text-xs">
                            ais-cookie-consent-v1
                          </td>
                          <td className="py-2 pr-3">Stores your cookie preferences</td>
                          <td className="py-2">12 months</td>
                        </tr>
                        <tr>
                          <td className="py-2 pr-3 font-mono text-xs">local storage</td>
                          <td className="py-2 pr-3">
                            Keeps your session state (form drafts, admin login)
                          </td>
                          <td className="py-2">Session</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div className="border-l-4 border-blue-500 pl-5 py-2 bg-blue-50/40 rounded-r-lg">
                    <h3 className="font-semibold text-slate-900 text-lg mb-2">
                      Analytics Cookies (PostHog) — optional
                    </h3>
                    <p className="text-sm mb-3">
                      We use <strong>PostHog</strong> to understand how visitors use the site
                      (which pages are viewed, which buttons clicked, general flow). This helps us
                      improve your experience. These cookies only run if you consent.
                    </p>
                    <table className="w-full text-sm border-collapse">
                      <thead>
                        <tr className="border-b border-slate-300 text-left">
                          <th className="py-2 pr-3 font-semibold">Provider</th>
                          <th className="py-2 pr-3 font-semibold">Purpose</th>
                          <th className="py-2 font-semibold">Duration</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="py-2 pr-3">PostHog (us.i.posthog.com)</td>
                          <td className="py-2 pr-3">
                            Anonymous usage analytics & session insights
                          </td>
                          <td className="py-2">Up to 12 months</td>
                        </tr>
                      </tbody>
                    </table>
                    <p className="text-xs text-slate-600 mt-3">
                      PostHog Privacy Policy:{' '}
                      <a
                        href="https://posthog.com/privacy"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-700 underline"
                      >
                        posthog.com/privacy
                      </a>
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-3">4. Your choices</h2>
                <p>
                  When you first visit our site, a banner asks you to accept or reject non-essential
                  cookies. You can change your mind any time by clicking the button below:
                </p>
                <Button
                  onClick={openCookiePreferences}
                  className="mt-4 bg-emerald-600 hover:bg-emerald-700"
                  data-testid="reopen-cookie-banner-btn"
                >
                  <Settings className="h-4 w-4 mr-2" /> Manage Cookie Preferences
                </Button>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-3">
                  5. Browser controls
                </h2>
                <p>
                  Most browsers let you delete cookies or block them entirely. Instructions for the
                  main browsers:
                </p>
                <ul className="list-disc pl-6 mt-2 space-y-1">
                  <li>
                    <strong>Chrome</strong>: Settings → Privacy and security → Cookies
                  </li>
                  <li>
                    <strong>Safari</strong>: Preferences → Privacy
                  </li>
                  <li>
                    <strong>Firefox</strong>: Options → Privacy & Security → Cookies
                  </li>
                  <li>
                    <strong>Edge</strong>: Settings → Cookies and site permissions
                  </li>
                </ul>
                <p className="mt-2 text-sm text-slate-600">
                  Note: Blocking all cookies may break parts of the site (e.g. the booking form).
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-3">6. Your rights</h2>
                <p>
                  Under UK GDPR, you have the right to access, rectify, erase, restrict, or object
                  to our processing of your personal data. To exercise any of these rights, contact
                  us at{' '}
                  <a
                    href="mailto:customersupport@aisexpress.co.uk"
                    className="text-emerald-700 underline"
                  >
                    customersupport@aisexpress.co.uk
                  </a>
                  . You also have the right to complain to the{' '}
                  <a
                    href="https://ico.org.uk"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-emerald-700 underline"
                  >
                    Information Commissioner's Office (ICO)
                  </a>
                  .
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-3">7. Changes to this policy</h2>
                <p>
                  We may update this Cookie Policy from time to time. The "Last updated" date at the
                  top of this page shows the most recent revision.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-3">8. Contact us</h2>
                <p>
                  Questions about this policy? Contact:
                  <br />
                  <strong>AIS Express</strong>
                  <br />
                  22 Osmans Close, Bracknell, RG42 7PX
                  <br />
                  Phone:{' '}
                  <a href="tel:01344508095" className="text-emerald-700 underline">
                    01344 508095
                  </a>
                  <br />
                  Email:{' '}
                  <a
                    href="mailto:customersupport@aisexpress.co.uk"
                    className="text-emerald-700 underline"
                  >
                    customersupport@aisexpress.co.uk
                  </a>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </>
  );
};

export default CookiePolicy;
